'use client';

import cx from 'classnames';
import { testDriveCenterImages } from '~/constants/dealership/testDriveCenter/assets';
import optimizeImage from '~/utilities/optimizeImage';
import styles from './TestDriveCenterFindIt.module.css';
import useStackedCards from '../hooks/useStackedCards';

const imageOptions = { format: 'auto', optimize: 'medium' } as const;

const cards = [
  {
    alt: 'A customer building a custom vehicle tour at the Start Here kiosk',
    heading: ['Personalized from the start'],
    image: testDriveCenterImages.findItCube,
    text: 'We’ll recommend vehicles based on your preferences and build a custom tour just for you.'
  },
  {
    alt: 'A customer viewing vehicle details on their phone next to a Jeep',
    heading: ['Everything in your pocket'],
    image: testDriveCenterImages.findItPocket,
    text: 'Your map, vehicle information, favorites, and test drives all live in one place, your phone.'
  },
  {
    alt: 'A customer opening the door of a brand-new Jeep in a test drive lane',
    heading: ['No pressure.', 'Just drive.'],
    image: testDriveCenterImages.findItDrive,
    text: 'Test drive as many vehicles as you like at your own pace, then complete your purchase online when you’re ready.'
  }
];

export default function TestDriveCenterFindIt() {
  const { listRef, lockupRef } = useStackedCards();

  // Each card pins via position: sticky at a slightly larger offset than the
  // one before it, so cards slide up and stack into a deck as the user
  // scrolls while the lockup stays pinned beside (or above) the stack.
  return (
    <section className="bg-canvas-default px-2xl py-7xl md:px-4xl md:py-8xl">
      <div
        className={cx(
          styles.layout,
          'mx-auto grid max-w-[1272px] gap-y-4xl lg:flex lg:flex-row-reverse lg:items-start lg:justify-between lg:gap-x-7xl'
        )}
      >
        <div
          className={cx(
            styles.lockup,
            'sticky lg:w-[360px] lg:shrink-0 lg:pt-8xl'
          )}
        >
          <div
            ref={lockupRef}
            className={cx(styles.lockupContent, 'flex flex-col gap-y-xl')}
          >
            <h2 className="text-text-strong t-brand-sm lg:t-brand-md">
              Find it. Drive it. Love it.
            </h2>
            <p className="text-text-default t-body-md lg:t-body-lg">
              Our brand new Test Drive Center brings the Carvana experience to
              life, with personalized recommendations, self-guided exploration,
              and hassle-free test drives.
            </p>
          </div>
        </div>

        <div
          ref={listRef}
          className={cx(
            styles.cardList,
            'flex w-full flex-col gap-y-4xl lg:max-w-[841px]'
          )}
        >
          {cards.map(card => (
            <article
              key={card.image}
              className={cx(
                styles.card,
                'sticky flex flex-col overflow-hidden rounded-xl bg-background-default-hover shadow-md',
                'md:h-[400px] md:flex-row md:items-stretch md:justify-between'
              )}
            >
              <div className="flex flex-col gap-y-lg p-2xl pb-3xl md:max-w-[380px] md:justify-center md:py-0 md:pl-5xl md:pr-0">
                <h3 className="text-text-strong t-brand-xs md:t-brand-sm">
                  {card.heading.map(line => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </h3>
                <p className="text-text-default t-body-sm">{card.text}</p>
              </div>
              <img
                src={optimizeImage(card.image, imageOptions)}
                alt={card.alt}
                className="h-[232px] w-full object-cover md:h-full md:w-[392px]"
                loading="lazy"
              />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
